import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { FileText, FileDown, Mail, Pencil } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import * as turf from "@turf/area";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "@turf/helpers";
import { groupLineItemsBySections } from "./../utils/lineItemSections";
import "mapbox-gl/dist/mapbox-gl.css";

// Sub-components
import QuotePageTopBar from "./QuoteView/QuotePageTopBar";
import QuoteInfoSection from "./QuoteView/QuoteInfoSection";
import QuoteMapSection from "./QuoteView/QuoteMapSection";
import QuoteLineItems from "./QuoteView/QuoteLineItems";
import QuoteSignAndPay from "./QuoteView/QuoteSignAndPay";
import PaymentsTimeline from "../components/PaymentsTimeline";
import JobCostsSection from "../components/JobCostsSection";
import SendQuoteEmailDialog from "../components/dialogs/SendQuoteEmailDialog";
import { useNavigate } from "react-router-dom";
import { isAdminLoggedIn } from "../lib/auth";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const NWI_TILE_URL =
  "https://fwspublicservices.wim.usgs.gov/wetlandsmapservice/rest/services/Wetlands/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:0";

export const calculateAcres = (polygon) => {
  if (!polygon || !polygon.coordinates) return 0;
  const feature = { type: "Feature", geometry: polygon };
  const areaInSquareMeters = turf.default(feature);
  return Math.round((areaInSquareMeters / 4046.86) * 100) / 100;
};

export const calculateTotalAcres = (polygons) => {
  if (!polygons || polygons.length === 0) return 0;
  return polygons.reduce((total, poly) => total + calculateAcres(poly), 0);
};

const QuoteViewPage = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [terms, setTerms] = useState("");
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showCashCheckInfo, setShowCashCheckInfo] = useState(false);
  const [optionalSelections, setOptionalSelections] = useState({});
  const [adjustedQuantities, setAdjustedQuantities] = useState({});
  const [signatureName, setSignatureName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const isAdmin = isAdminLoggedIn();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [showWetlands, setShowWetlands] = useState(true);

  useEffect(() => {
    fetchQuote();
    fetchTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  useEffect(() => {
    if (quote?.polygon_data && mapContainer.current && !map.current && MAPBOX_TOKEN) {
      initializeMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      const quoteData = response.data;
      setQuote(quoteData);
      const initialSelections = {};
      const initialQuantities = {};
      quoteData.line_items?.forEach(item => {
        if (item.optional) initialSelections[item.id] = item.accepted !== false;
        if (item.customer_adjustable_qty) initialQuantities[item.id] = item.quantity ?? 0;
      });
      setOptionalSelections(initialSelections);
      setAdjustedQuantities(initialQuantities);
    } catch {
      toast.error("Quote not found");
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/terms`);
      setTerms(response.data.terms);
    } catch (err) {
      console.error("Failed to load terms:", err?.message);
    }
  };

  const handleOptionalToggle = useCallback((itemId, checked) => {
    setOptionalSelections(prev => ({ ...prev, [itemId]: checked }));
  }, []);

  const handleQuantityChange = useCallback((itemId, qty) => {
    setAdjustedQuantities(prev => ({ ...prev, [itemId]: qty }));
  }, []);

  const calculateDynamicSubtotal = useCallback(() => {
    if (!quote?.line_items) return 0;
    const lineTotal = (item) => {
      if (item.customer_adjustable_qty && adjustedQuantities[item.id] != null) {
        return (Number(adjustedQuantities[item.id]) || 0) * (item.unit_price || 0);
      }
      return item.total || 0;
    };
    // Section-aware: a declined optional section => 0; a section lump sum => its
    // flat amount; otherwise sum items, skipping declined optional items.
    const groups = groupLineItemsBySections(quote.line_items);
    return groups.reduce((total, g) => {
      const h = g.header;
      if (h && h.optional && optionalSelections[h.id] === false) return total;
      if (h && h.section_lump_sum != null && h.section_lump_sum !== "") {
        return total + (Number(h.section_lump_sum) || 0);
      }
      const secSum = g.items.reduce((sum, item) => {
        if (!item.optional) return sum + lineTotal(item);
        return optionalSelections[item.id] ? sum + lineTotal(item) : sum;
      }, 0);
      return total + secSum;
    }, 0);
  }, [quote, optionalSelections, adjustedQuantities]);

  const dynamicSubtotal = calculateDynamicSubtotal();
  let discountAmount = 0;
  if (quote?.discount_type && quote?.discount_value) {
    discountAmount = quote.discount_type === "percentage"
      ? dynamicSubtotal * (quote.discount_value / 100)
      : quote.discount_value;
  }
  const discountedSubtotal = dynamicSubtotal - discountAmount;
  const hasOptionalItems = quote?.line_items?.some(item => item.optional);

  const handleOpenPdf = useCallback(() => {
    if (!quote?.quote_pdf) return;
    // Browsers (Chrome 60+, Firefox, Safari) block top-level navigation to
    // data: URLs, so window.open(dataUrl) opens a blank tab. Convert to a
    // Blob URL — same content, but allowed for navigation.
    try {
      const dataUrl = quote.quote_pdf;
      if (!dataUrl.startsWith("data:")) {
        // Already a real URL (legacy fallback)
        const win = window.open(dataUrl, "_blank");
        if (win) win.focus();
        return;
      }
      const [meta, b64] = dataUrl.split(",");
      const mime = (meta.match(/data:([^;]+)/) || [, "application/pdf"])[1];
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, "_blank");
      if (win) {
        win.focus();
      } else {
        // Popup blocked — fall back to same-tab navigation
        window.location.href = blobUrl;
      }
      // Release the blob a minute later (after the new tab has loaded)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      console.error("Failed to open PDF", e);
    }
  }, [quote]);

  const reloadQuoteViewLayers = () => {
    if (!map.current || !quote?.polygon_data) return;
    const m = map.current;
    const polys = Array.isArray(quote.polygon_data) ? quote.polygon_data : [quote.polygon_data];

    if (!m.getSource("nwi-wetlands")) {
      m.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
      m.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });
      if (!showWetlands) m.setLayoutProperty("nwi-wetlands-layer", "visibility", "none");
    }

    polys.forEach((poly, index) => {
      const sourceId = `property-${index}`;
      if (poly?.coordinates && !m.getSource(sourceId)) {
        m.addSource(sourceId, { type: "geojson", data: { type: "Feature", geometry: poly } });
        m.addLayer({ id: `${sourceId}-fill`, type: "fill", source: sourceId, paint: { "fill-color": "#dc2626", "fill-opacity": 0.3 } });
        m.addLayer({ id: `${sourceId}-outline`, type: "line", source: sourceId, paint: { "line-color": "#dc2626", "line-width": 3 } });
      }
    });

    if (quote.property_address) {
      axios.post(`${API}/parcels/by-address`, { address: quote.property_address }).then((resp) => {
        if (resp.data.features?.length > 0 && map.current && !map.current.getSource("parcels")) {
          map.current.addSource("parcels", { type: "geojson", data: { type: "FeatureCollection", features: resp.data.features } });
          map.current.addLayer({ id: "parcels-fill", type: "fill", source: "parcels", paint: { "fill-color": "#4169E1", "fill-opacity": 0.1 } });
          map.current.addLayer({ id: "parcels-outline", type: "line", source: "parcels", paint: { "line-color": "#4169E1", "line-width": 2, "line-opacity": 0.7 } });
        }
      }).catch(() => {});
    }
  };

  const initializeMap = () => {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const polygons = Array.isArray(quote.polygon_data) ? quote.polygon_data : [quote.polygon_data];
    const allCoords = [];
    polygons.forEach(poly => { if (poly?.coordinates?.[0]) allCoords.push(...poly.coordinates[0]); });

    let center = [-79.0193, 35.7596];
    if (allCoords.length > 0) {
      const lngs = allCoords.map(c => c[0]);
      const lats = allCoords.map(c => c[1]);
      center = [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center,
      zoom: 14,
    });

    map.current.on("load", async () => {
      if (!map.current.getSource("nwi-wetlands")) {
        map.current.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
        map.current.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });
      }

      if (quote.property_address) {
        try {
          const response = await axios.post(`${API}/parcels/by-address`, { address: quote.property_address });
          if (response.data.features?.length > 0) {
            const geocoded = response.data.geocoded;
            let selectedParcel = null;
            const neighborParcels = [];
            if (geocoded?.lat && geocoded?.lon) {
              const searchPoint = point([geocoded.lon, geocoded.lat]);
              for (const feature of response.data.features) {
                if (feature.geometry?.type === "Polygon") {
                  if (booleanPointInPolygon(searchPoint, feature) && !selectedParcel) selectedParcel = feature;
                  else neighborParcels.push(feature);
                } else if (feature.geometry?.type === "MultiPolygon") {
                  let found = false;
                  for (const coords of feature.geometry.coordinates) {
                    if (booleanPointInPolygon(searchPoint, { type: "Feature", geometry: { type: "Polygon", coordinates: coords } })) {
                      found = true; break;
                    }
                  }
                  if (found && !selectedParcel) selectedParcel = feature;
                  else neighborParcels.push(feature);
                }
              }
              if (!selectedParcel) { selectedParcel = response.data.features[0]; neighborParcels.push(...response.data.features.slice(1)); }
            } else {
              selectedParcel = response.data.features[0];
              neighborParcels.push(...response.data.features.slice(1));
            }

            if (neighborParcels.length > 0) {
              map.current.addSource("neighbor-parcels", { type: "geojson", data: { type: "FeatureCollection", features: neighborParcels } });
              map.current.addLayer({ id: "neighbor-parcels-fill", type: "fill", source: "neighbor-parcels", paint: { "fill-color": "#4169E1", "fill-opacity": 0.05 } });
              map.current.addLayer({ id: "neighbor-parcels-line", type: "line", source: "neighbor-parcels", paint: { "line-color": "#4169E1", "line-width": 1.5, "line-opacity": 0.5 } });
            }
            map.current.addSource("selected-parcel", { type: "geojson", data: { type: "FeatureCollection", features: [selectedParcel] } });
            map.current.addLayer({ id: "selected-parcel-fill", type: "fill", source: "selected-parcel", paint: { "fill-color": "#4169E1", "fill-opacity": 0.15 } });
            map.current.addLayer({ id: "selected-parcel-line", type: "line", source: "selected-parcel", paint: { "line-color": "#4169E1", "line-width": 3.5, "line-opacity": 1.0 } });
          }
        } catch (err) {
          console.error("Failed to fetch parcel data:", err?.message);
        }
      }

      if (quote.additional_properties?.length > 0) {
        for (let i = 0; i < quote.additional_properties.length; i++) {
          const prop = quote.additional_properties[i];
          if (prop.geometry) {
            const sid = `additional-parcel-${i}`;
            map.current.addSource(sid, { type: "geojson", data: { type: "Feature", geometry: prop.geometry } });
            map.current.addLayer({ id: `${sid}-fill`, type: "fill", source: sid, paint: { "fill-color": "#4169E1", "fill-opacity": 0.15 } });
            map.current.addLayer({ id: `${sid}-line`, type: "line", source: sid, paint: { "line-color": "#4169E1", "line-width": 3.5, "line-opacity": 1.0 } });
          } else if (prop.address) {
            try {
              const resp = await axios.post(`${API}/parcels/by-address`, { address: prop.address });
              if (resp.data.features?.length > 0 && resp.data.geocoded?.lat) {
                const sp = point([resp.data.geocoded.lon, resp.data.geocoded.lat]);
                for (const feature of resp.data.features) {
                  if (feature.geometry?.type === "Polygon" && booleanPointInPolygon(sp, feature)) {
                    const sid = `additional-parcel-${i}`;
                    map.current.addSource(sid, { type: "geojson", data: { type: "Feature", geometry: feature.geometry } });
                    map.current.addLayer({ id: `${sid}-fill`, type: "fill", source: sid, paint: { "fill-color": "#4169E1", "fill-opacity": 0.15 } });
                    map.current.addLayer({ id: `${sid}-line`, type: "line", source: sid, paint: { "line-color": "#4169E1", "line-width": 3.5, "line-opacity": 1.0 } });
                    break;
                  }
                }
              }
            } catch (err) {
              console.error(`Failed to fetch parcel for additional property ${i}:`, err?.message);
            }
          }
        }
      }

      polygons.forEach((poly, index) => {
        if (!poly?.coordinates) return;
        const acres = calculateAcres(poly);
        const coords = poly.coordinates[0];
        const centroid = coords.reduce((acc, c) => [acc[0] + c[0], acc[1] + c[1]], [0, 0]).map(s => s / coords.length);

        map.current.addSource(`property-${index}`, {
          type: "geojson",
          data: { type: "Feature", geometry: poly, properties: { acres, label: poly._name || `Area ${index + 1}` } },
        });
        map.current.addLayer({ id: `property-fill-${index}`, type: "fill", source: `property-${index}`, paint: { "fill-color": "#dc2626", "fill-opacity": 0.3 } });
        map.current.addLayer({ id: `property-outline-${index}`, type: "line", source: `property-${index}`, paint: { "line-color": "#dc2626", "line-width": 3 } });

        map.current.addSource(`label-${index}`, {
          type: "geojson",
          data: { type: "Feature", geometry: { type: "Point", coordinates: centroid }, properties: { label: poly._name || `Area ${index + 1}`, acres: acres.toFixed(2) } },
        });
        map.current.addLayer({ id: `label-bg-${index}`, type: "circle", source: `label-${index}`, paint: { "circle-radius": 28, "circle-color": "#dc2626", "circle-opacity": 0.9 } });
        map.current.addLayer({
          id: `label-text-${index}`, type: "symbol", source: `label-${index}`,
          layout: { "text-field": ["concat", ["get", "label"], "\n", ["get", "acres"], " ac"], "text-size": 12, "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"], "text-anchor": "center", "text-allow-overlap": true },
          paint: { "text-color": "#ffffff", "text-halo-color": "#dc2626", "text-halo-width": 1 },
        });
      });

      if (allCoords.length > 0) {
        const bounds = allCoords.reduce((b, c) => b.extend(c), new mapboxgl.LngLatBounds(allCoords[0], allCoords[0]));
        map.current.fitBounds(bounds, { padding: 50 });
      }
    });
  };

  const handleSign = async () => {
    if (!signatureName.trim()) { toast.error("Please enter your full name"); return; }
    if (!acceptTerms) { toast.error("Please accept the terms and conditions"); return; }
    setSigning(true);
    try {
      if (hasOptionalItems) {
        await axios.post(`${API}/quotes/${quoteId}/select-items`, { selections: optionalSelections, quantities: adjustedQuantities });
      }
      await axios.post(`${API}/quotes/${quoteId}/sign`, { signature_name: signatureName, accept_terms: acceptTerms });
      toast.success("Quote signed successfully");
      fetchQuote();
    } catch {
      toast.error("Failed to sign quote");
    } finally {
      setSigning(false);
    }
  };

  const handlePayment = async (requestedPaymentType = null) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/quotes/${quoteId}/payment/create-session`, {
        origin_url: window.location.origin,
        payment_type: requestedPaymentType || paymentType,
      });
      if (response.data.url) window.location.href = response.data.url;
    } catch {
      toast.error("Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  const handleCashCheckPayment = async () => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/cash-check`);
      toast.success("Thank you! We'll be in touch with payment instructions.");
      fetchQuote();
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00341d]" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Quote Not Found</h2>
            <p className="text-zinc-500">This quote may have been removed or is no longer available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSigned = !!quote.signature_name;
  const isPaid = quote.payment_status === "paid";
  const isDepositPaid = quote.deposit_paid || quote.payment_status === "deposit_paid";
  const isBalancePaid = quote.balance_paid;

  const savedDiscount = quote.discount_amount || 0;
  const coItems = quote.balance_change_orders || [];
  const coTotal = Number(quote.balance_change_orders_total) || 0;
  let effectiveSubtotal;
  if (isSigned) {
    effectiveSubtotal = quote.subtotal - savedDiscount;
  } else if (hasOptionalItems) {
    effectiveSubtotal = discountedSubtotal;
  } else {
    effectiveSubtotal = quote.subtotal - savedDiscount;
  }
  const r2 = (n) => Math.floor(n * 100) / 100;
  const isFullPayment = r2(effectiveSubtotal * 1.03) <= 3000 || quote.require_full_payment;
  const totalWithFee = r2(effectiveSubtotal * 1.03);
  const ccFeeTotal = r2(totalWithFee - effectiveSubtotal);
  // BASE amounts (no fee) — used when the customer hasn't yet chosen card payment
  const depositBase = isFullPayment ? effectiveSubtotal : r2(effectiveSubtotal / 2);
  const balanceBase = isFullPayment ? 0 : r2(effectiveSubtotal - depositBase);
  // WITH-FEE amounts — only displayed once the card flow is in view
  const depositAmount = isFullPayment ? totalWithFee : r2(totalWithFee / 2);
  const balanceAmount = isFullPayment ? 0 : r2(totalWithFee / 2);
  // Fee on the current charge (deposit slice or full)
  const depositCcFee = r2(depositAmount - depositBase);

  let paymentAmount, paymentLabel, paymentType, paymentBase, paymentFee;
  if (isDepositPaid && !isBalancePaid && !isFullPayment) {
    // BUG FIX (Feb 2026): previously hardcoded balanceBase = effectiveSubtotal / 2,
    // which was wrong whenever the admin recorded a non-50% partial payment (e.g.
    // a $9k check on a $18k quote). The remaining balance must be derived from the
    // ACTUAL amount paid:
    //   - cash/check: quote.deposit_amount is base dollars
    //   - card (Stripe): quote.deposit_amount is with-fee (set in payments webhook),
    //                    so divide by 1.03 to recover the base.
    const rawDeposit = Number(quote.deposit_amount) || 0;
    const actualPaidBase = quote.payment_method === "card"
      ? r2(rawDeposit / 1.03)
      : rawDeposit;
    const realBalanceBase = Math.max(r2(effectiveSubtotal - actualPaidBase), 0) + coTotal;
    const realBalanceFee = r2(realBalanceBase * 0.03);
    const realBalanceTotal = r2(realBalanceBase + realBalanceFee);
    paymentAmount = realBalanceTotal; paymentLabel = "Balance Due"; paymentType = "balance";
    paymentBase = realBalanceBase; paymentFee = realBalanceFee;
  } else if (isFullPayment) {
    paymentAmount = totalWithFee; paymentLabel = "Full Payment"; paymentType = "full";
    paymentBase = effectiveSubtotal; paymentFee = ccFeeTotal;
  } else {
    paymentAmount = depositAmount; paymentLabel = "50% Deposit"; paymentType = "deposit";
    paymentBase = depositBase; paymentFee = depositCcFee;
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="quote-view-page">
      <QuotePageTopBar quote={quote} />

      {isAdmin && (
        <div className="bg-amber-50 border-y border-amber-200" data-testid="admin-action-bar">
          <div className="max-w-5xl mx-auto px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Admin tools</span>
              <span className="text-amber-700 ml-2">— Visible only to logged-in admins.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/quotes/${quote.id}/edit`)}
                className="border-[#00341d]/30 text-[#00341d] hover:bg-[#00341d]/5"
                data-testid="admin-edit-quote-btn"
              >
                <Pencil className="w-4 h-4 mr-2" /> Edit Quote
              </Button>
              <Button
                onClick={() => {
                  if (!quote.customer_email) {
                    toast.error("Customer email is missing");
                    return;
                  }
                  setSendDialogOpen(true);
                }}
                className="bg-[#00341d] hover:bg-[#002816] text-white"
                data-testid="admin-send-quote-btn"
                disabled={!quote.customer_email}
                title={!quote.customer_email ? "Add a customer email to send" : ""}
              >
                <Mail className="w-4 h-4 mr-2" /> Send to Customer
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Card className="bg-white shadow-2xl rounded-none overflow-hidden border-t-8 border-[#00341d]">
          <QuoteInfoSection
            quote={quote}
            calculateAcres={calculateAcres}
            calculateTotalAcres={calculateTotalAcres}
          />

          {/* Full-width map */}
          {quote.polygon_data && MAPBOX_TOKEN && (
            <QuoteMapSection
              mapContainerRef={mapContainer}
              mapRef={map}
              quote={quote}
              showWetlands={showWetlands}
              setShowWetlands={setShowWetlands}
              onStyleLoad={reloadQuoteViewLayers}
              calculateAcres={calculateAcres}
              calculateTotalAcres={calculateTotalAcres}
            />
          )}

          {/* PDF Download */}
          {quote.quote_pdf && (
            <div className="p-8 border-b border-zinc-200 bg-gradient-to-r from-[#00341d]/5 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#00341d] flex items-center justify-center">
                    <FileDown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-[#00341d]">Detailed Quote Document</h3>
                    <p className="text-sm text-zinc-500">{quote.quote_pdf_filename || "Quote PDF"}</p>
                  </div>
                </div>
                <Button onClick={handleOpenPdf} className="bg-[#00341d] hover:bg-[#002816] text-white" data-testid="download-pdf-button">
                  <FileText className="w-4 h-4 mr-2" />
                  View PDF
                </Button>
              </div>
            </div>
          )}

          <QuoteLineItems
            quote={quote}
            isSigned={isSigned}
            hasOptionalItems={hasOptionalItems}
            optionalSelections={optionalSelections}
            handleOptionalToggle={handleOptionalToggle}
            adjustedQuantities={adjustedQuantities}
            handleQuantityChange={handleQuantityChange}
            dynamicSubtotal={dynamicSubtotal}
            discountAmount={discountAmount}
            discountedSubtotal={discountedSubtotal}
          />

          {/* Payment Terms */}
          <div className="p-8 bg-amber-50 border-b border-amber-200">
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-amber-900 mb-4">
              Payment Terms
            </h3>
            {isFullPayment ? (
              <div className="space-y-2">
                <p className="text-amber-800">
                  <strong>Full payment of ${effectiveSubtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong> is due upon signing before work commences.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-amber-800">
                  <strong>50% Deposit:</strong> ${r2(effectiveSubtotal / 2)?.toLocaleString(undefined, { minimumFractionDigits: 2 })} due upon signing
                </p>
                <p className="text-amber-800">
                  <strong>50% Balance:</strong> ${r2(effectiveSubtotal / 2)?.toLocaleString(undefined, { minimumFractionDigits: 2 })} due upon completion
                </p>
              </div>
            )}
          </div>

          {/* Start Date — first_available (no date), preferred/legacy (with date), or valid_for (silent on start date) */}
          {quote.start_date_type === "valid_for" ? (
            <div className="p-8 border-b border-zinc-200 bg-green-50" data-testid="quote-valid-for-block">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00341d] mb-4">
                Quote Validity
              </h3>
              <p className="text-lg font-semibold text-green-800">
                Valid for {quote.valid_days || 30} days
              </p>
              <p className="text-xs text-green-700/80 mt-2 leading-relaxed max-w-2xl">
                This quote is valid for {quote.valid_days || 30} days from the date it was issued. Please accept before it expires to lock in this pricing.
              </p>
            </div>
          ) : (quote.start_date_type === "first_available" || quote.expected_start_date) && (
            <div className="p-8 border-b border-zinc-200 bg-green-50" data-testid="quote-start-date-block">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00341d] mb-4">
                {quote.start_date_type === "first_available"
                  ? "Start Date"
                  : quote.start_date_type === "preferred"
                    ? "Preferred Start Date"
                    : "Expected Start Date"}
              </h3>
              <p className="text-lg font-semibold text-green-800">
                {quote.start_date_type === "first_available" ? "First Available" : (() => {
                  const [year, month, day] = quote.expected_start_date.split('-').map(Number);
                  const date = new Date(year, month - 1, day);
                  return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
                })()}
              </p>
              <p className="text-xs text-green-700/80 mt-2 leading-relaxed max-w-2xl">
                Start dates are estimates and may shift due to weather, equipment availability, or completion of work for prior clients. We'll make every effort to honor your preferred schedule.
              </p>
            </div>
          )}

          {/* Notes */}
          {(quote.shared_notes || quote.notes || quote.notes_image) && (
            <div className="p-8 border-b border-zinc-200 bg-zinc-50">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00341d] mb-4">Notes</h3>
              {(quote.shared_notes || quote.notes) && <p className="text-zinc-700 whitespace-pre-wrap">{quote.shared_notes || quote.notes}</p>}
              {quote.notes_image && (
                <div className="mt-4">
                  <img src={quote.notes_image} alt="Notes attachment" className="max-w-full md:max-w-lg rounded-lg border border-zinc-200" />
                </div>
              )}
            </div>
          )}

          {/* Admin-only Payment History timeline (hidden if no payments yet) */}
          {isAdmin && <PaymentsTimeline quoteId={quote.id} />}

          {/* Admin-only Job Profitability tracker (hours, diesel, photos) */}
          {isAdmin && <JobCostsSection quoteId={quote.id} />}

          <QuoteSignAndPay
            quote={quote}
            isSigned={isSigned}
            isPaid={isPaid}
            isDepositPaid={isDepositPaid}
            isFullPayment={isFullPayment}
            paymentAmount={paymentAmount}
            paymentLabel={paymentLabel}
            paymentBase={paymentBase}
            paymentFee={paymentFee}
            depositAmount={depositAmount}
            depositBase={depositBase}
            balanceAmount={balanceAmount}
            balanceBase={balanceBase}
            changeOrders={coItems}
            changeOrdersTotal={coTotal}
            terms={terms}
            showTerms={showTerms}
            setShowTerms={setShowTerms}
            signatureName={signatureName}
            setSignatureName={setSignatureName}
            acceptTerms={acceptTerms}
            setAcceptTerms={setAcceptTerms}
            signing={signing}
            processingPayment={processingPayment}
            showCashCheckInfo={showCashCheckInfo}
            setShowCashCheckInfo={setShowCashCheckInfo}
            handleSign={handleSign}
            handlePayment={handlePayment}
            handleCashCheckPayment={handleCashCheckPayment}
          />
        </Card>

        <div className="text-center mt-8 text-zinc-500 text-sm">
          <p>Land ClearCo, LLC &copy; {new Date().getFullYear()}</p>
          <p className="mt-1">Professional Land Clearing Services</p>
        </div>
      </main>

      {isAdmin && (
        <SendQuoteEmailDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          quote={quote}
          onSent={fetchQuote}
        />
      )}
    </div>
  );
};

export default QuoteViewPage;
